import React, { useCallback, useEffect, useRef, useState } from 'react';
import SpecialUsersTable from '../common/SpecialUsersTable';
import Badge from 'components/Badge/Badge';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import AddUserDialog from 'views/Users/common/AddUserDialog';
import { DepartmentsCell, LinkCell, UserNameCell } from 'components/Cells/Cells';
import AssignmentInd from '@material-ui/icons/AssignmentIndOutlined';
import { AccountsService } from 'services/accounts/accounts';
import { UsersService } from 'services/users/users';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch, useSelector } from 'react-redux';
import invalidateCache from 'utils/api/invalidateCache';
import { finalize } from 'rxjs/operators';
import { setToast } from 'redux/modules/toast/actions';
import {
	accountPermissionsSelector,
	departmentPermissionsSelector,
	userTypeSelector,
} from 'redux/modules/session/selectors';
import { translate } from 'components/Translation';
import { exportToXLS } from 'utils/export/export';
import { hasAccess } from 'utils/permissions/permissions';
import moment from 'moment';
import _ from 'lodash';
import { SingleUser } from 'utils/api/models/users/singleUser';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';

interface IManager extends SingleUser {
	department?: SingleDepartment;
	permissionDepartments: any[];
	accessible: boolean;
}

interface IOrganizationManagersProps {
	departments: SingleDepartment[];
	accounts: SingleCorpAccount[];
	match: {
		params: {
			accountId: string;
		};
		url: string;
	};
}

const OrganizationManagers = (props: IOrganizationManagersProps) => {
	const [managers, setManagers] = useState<IManager[]>([]);
	const [filteredManagers, setFilteredManagers] = useState<IManager[]>([]);
	const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
	const [searchInputValue, setSearchInputValue] = useState('');
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const { accountId } = props.match.params;
	const { departments, accounts } = props;
	const dispatch = useDispatch();
	const loggedUserId = useSelector(({ session }: { session: any }) => session.user.userId);
	const departmentPermissions = useSelector(departmentPermissionsSelector);
	const accountPermissions = useSelector(accountPermissionsSelector);
	const loggedUserType = useSelector(userTypeSelector);
	const isMounted = useRef(true);

	const findDepartment = useCallback(
		(departmentId: string) => {
			return departments.find((department) => department.deptId === departmentId);
		},
		[departments]
	);

	const userAddedSuccessfully = () => {
		fetchManagers(true);
	};

	const findAccount = (id: string) => {
		return accounts.find((account) => account.accountId === id);
	};

	const userName = (user: SingleUser) => {
		let name = user.userName;
		if (user.userId === loggedUserId) {
			name += ` (${translate('you')})`;
		}
		return name;
	};

	const fetchManagers = useCallback(
		(hasChanged: boolean) => {
			dispatch(showLoadingIndicator());
			AccountsService.getCorpAccountManagers(accountId)
				.pipe(finalize(() => dispatch(hideLoadingIndicator())))
				.subscribe(({ managers: fetchedManagers }: { managers: SingleUser[] }) => {
					if (isMounted.current) {
						setManagers((managers: SingleUser[]) => {
							const newManagers = _.differenceBy(fetchedManagers, managers, 'userId');
							return [
								...managers,
								...newManagers.map((manager) => ({
									...manager,
									new: hasChanged,
								})),
							].map((manager) => {
								const department = findDepartment(manager.departmentId);
								const permissionDepartments = manager.permissionDepartmentIds
									.map((id: string) => findDepartment(id))
									.filter(Boolean);
								return {
									...manager,
									department,
									permissionDepartments,
									accessible: hasAccess(
										manager,
										loggedUserType,
										accountPermissions,
										departmentPermissions
									),
								};
							});
						});
					}
				});
		},
		[dispatch, accountId, findDepartment, loggedUserType, accountPermissions, departmentPermissions]
	);

	useEffect(() => {
		fetchManagers(false);
		window.scrollTo({
			top: 0
		});
	}, [fetchManagers]);

	useEffect(() => {
		const lowerCaseSearchInput = searchInputValue.toLowerCase();
		const _filteredMangers = managers.filter(
			(manager) => manager.userName.toLowerCase().indexOf(lowerCaseSearchInput) >= 0
		);
		setFilteredManagers(_filteredMangers);
	}, [managers, searchInputValue]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const exportToXls = useCallback(() => {
		const preparedData = (selectedIndexes.length === 0
			? filteredManagers
			: selectedIndexes.map((index) => filteredManagers[index])
		).map((manager) => ({
			'User name': manager.userName,
			'Department name': manager.department ? manager.department.name : '',
			Permissions: _.toString(
				manager.permissionDepartments.map((department: SingleDepartment) => department.name)
			),
		}));

		exportToXLS(preparedData, `managers ${moment().format('LLL')}`);
	}, [selectedIndexes, filteredManagers]);

	const deleteItems = () => {
		const userIds = _.chain(selectedIndexes)
			.map((index) => filteredManagers[index])
			.map((manager) => manager.userId)
			.value();
		dispatch(showLoadingIndicator());
		UsersService.deleteAccountPermissionsForCorpUsers({
			accountId,
			userIds,
		})
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(({ response }) => {
				if (response.deleteAccountPermissionsForCorpUsersResponse.result) {
					setSelectedIndexes([]);
					setManagers((managers) =>
						managers
							.filter((manager) => userIds.indexOf(manager.userId) < 0)
							.map((manager) => ({ ...manager, new: false }))
					);
					invalidateCache(
						[
							'getCorpAccountDetails',
							'getCorpAccountManagers',
							'getDepartmentsForCurrentUser',
							'getDepartmentDetails',
							'getCorpUserDetails',
						],
						true
					);
					dispatch(
						setToast({
							error: false,
							label: translate('organizations.managers.deleted'),
							actionType: translate('deleted'),
						})
					);
				}
			});
	};

	return (
		<div>
			<BreadcrumbsItem to={props.match.url}>{translate('organizations.organization.managers')}</BreadcrumbsItem>
			<SpecialUsersTable
				selectedIndexes={selectedIndexes}
				button={{
					text: translate('add.manager'),
					onClick: () => setIsDialogOpen(true),
					action: 'organizations:add-manager',
					id: 'add-manager-button',
				}}
				exportToXLS={exportToXls}
				searchInputValue={searchInputValue}
				setSearchInputValue={(value) => setSearchInputValue(value)}
				setSelectedIndexes={setSelectedIndexes}
				deleteItemsAction="organization-managers:delete-managers"
				deleteItems={deleteItems}
				header={translate('organizations.organization.managers')}
				data={filteredManagers}
				columns={[
					{
						Header: translate('table.user.name'),
						Cell: ({ original }: any) => (
							<>
								<ConditionalRender
									show={original.accessible}
									onTrue={() => (
										<LinkCell
											path={`/dashboard/users/${original.userId}/user-details`}
											text={userName(original)}
											icon={<AssignmentInd />}
										/>
									)}
									onFalse={() => <UserNameCell text={userName(original)} type={'MANAGER'} />}
								/>
								<ConditionalRender
									show={!!original.department}
									onTrue={() => (
										<Badge hexColor={original.department.uiColor} filled>
											{original.department.name}
										</Badge>
									)}
								/>
							</>
						),
						accessor: 'userName',
						width: 250,
					},
					{
						Header: translate('table.permissions'),
						Cell: ({ original }: any) => (
							<DepartmentsCell
								departments={original.permissionDepartments}
								maxNumberOfBadges={2}
								maxBadgeWidth={120}
							/>
						),
						accessor: 'permissions',
						minWidth: 290,
					},
				]}
			/>
			<AddUserDialog
				isDialogOpen={isDialogOpen}
				setIsDialogOpen={(value) => setIsDialogOpen(value)}
				dialogHeader={translate('add.manager')}
				userType="MANAGER"
				accounts={accounts}
				departments={departments as any}
				account={findAccount(accountId)}
				userAddedSuccessfully={userAddedSuccessfully}
				toastSuccessMessage={translate('manager.user.added')}
			/>
		</div>
	);
};

export default OrganizationManagers;
